import { useCallback, useLayoutEffect, useRef } from 'react'
import { useThemeOptional } from '@papan/core-ui/ui'
import type { TextModifierHandle } from '../text-modifier-types.js'

/**
 * #987 — the person's "Format on save" setting (Settings → Editor, off by
 * default): an explicit save (Ctrl/Cmd+S, the Save button, Save All) formats
 * the document first, then saves. Quietly: a file that can't be formatted (a
 * syntax error, a language with no formatter) is saved as it is, without a
 * message on every save. With the setting off, or no editor surface (a Run or
 * preview view that unmounted it), it is the plain save.
 *
 * The format gets FORMAT_ON_SAVE_WAIT_MS: a slow drive walk for the project's
 * settings, the first Python format's download or a stuck worker never holds a
 * save back longer than that — the save goes ahead with whatever is there.
 * Accepted: a version preview or conflict cover that opens inside that window
 * doesn't stop the save the person already pressed (the host checked it at the
 * press, as before); and a format that lands after the wait shows as an unsaved
 * change, saved by the next save. A save still waiting when the editor goes away
 * (Cmd+S then closing the tab, switching file) goes out at once, unformatted:
 * from a layout-effect cleanup, which runs before the host's live connection
 * closes — later, the save would be a silent no-op.
 */
export const FORMAT_ON_SAVE_WAIT_MS = 2500

export function useSaveWithFormat(save: () => void, editor: React.RefObject<TextModifierHandle | null>): () => void {
  const on = useThemeOptional()?.settings.formatOnSave === true
  const latest = useRef({ save, on })
  latest.current = { save, on }
  const pendingSave = useRef<(() => void) | null>(null)
  useLayoutEffect(() => () => { pendingSave.current?.() }, [])
  return useCallback(() => {
    const { save: plainSave, on: formatFirst } = latest.current
    const format = editor.current?.format
    if (!formatFirst || !format) {
      plainSave()
      return
    }
    let saved = false
    const saveOnce = () => {
      if (saved) return
      saved = true
      clearTimeout(timer)
      if (pendingSave.current === saveOnce) pendingSave.current = null
      latest.current.save()
    }
    const timer = setTimeout(saveOnce, FORMAT_ON_SAVE_WAIT_MS)
    pendingSave.current = saveOnce
    void format({ quiet: true }).catch(() => {}).finally(saveOnce)
  }, [editor])
}
